import MyProperty from "./MyProperty";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Grid2,
  Snackbar,
  Typography,
} from "@mui/material";
import { useGetMyPropertiesQuery } from "./propertiesApiSlice";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const MyPropertiesList = () => {
  const [page, setPage] = useState(1);
  const [properties, setProperties] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all"); // all, published, unpublished

  const { data, refetch, isFetching, isError, error, isSuccess } =
    useGetMyPropertiesQuery(
      { status: filterStatus, page: page },
      {
        skip: !page,
        refetchOnMountOrArgChange: true,
      } // Avoid re-fetching on initial mount
    );

  const hasMore = data?.hasMore ?? false;

  const location = useLocation();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Track the latest requested page to guard against out-of-order responses (optional but helpful)
  const latestRequestedPageRef = useRef(page);

  useEffect(() => {
    // when page changes, record it as latest requested
    latestRequestedPageRef.current = page;
  }, [page]);

  useEffect(() => {
    if (location.state?.message) {
      setSnackbar({
        open: true,
        message: location.state.message,
        severity: location.state.severity || "info",
      });
    }

    if (location.state?.refreshTrigger) {
      refetch();
      window.history.replaceState({}, ""); // Clear state
    }
  }, [location.state, refetch]);

  // // Update the properties list when data changes
  // useEffect(() => {
  //   if (data && data.properties) {
  //     setProperties((prev) => [...prev, ...data.properties]);
  //   }
  // }, [data]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPage(1);
    setProperties([]);
  }, [filterStatus]);

  // Update the properties list when data changes.
  // Merge pages but dedupe by id.
  useEffect(() => {
    if (!data) return;

    // adapt to response shape: try common keys
    const incoming = data.properties ?? data.ownerProperties ?? [];

    // If no incoming items, do nothing
    if (!Array.isArray(incoming) || incoming.length === 0) {
      return;
    }

    // Guard: only process this response if it corresponds to the latest requested page.
    // This avoids applying a stale earlier response that arrived late.
    // If you don't provide page in response, rely on latestRequestedPageRef to compare.
    // (We assume the current `page` is the page we requested for this fetch.)
    if (latestRequestedPageRef.current !== page) {
      // Stale response — ignore
      return;
    }

    setProperties((prev) => {
      if (page === 1) {
        // Replace completely on first page
        return incoming;
      }

      // Append deduped: build map of existing ids
      const map = new Map();
      prev.forEach((p) => {
        const id = p._id ?? p.id;
        if (id) map.set(String(id), p);
      });

      // Add/overwrite with incoming (so if server updated a property we replace it)
      incoming.forEach((p) => {
        const id = p._id ?? p.id;
        map.set(String(id), p);
      });

      return Array.from(map.values());
    });
  }, [data, page]);

  // Setup Intersection Observer for infinite scrolling
  const observer = useRef();
  const lastPropertyRef = useCallback(
    (node) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1); // Trigger the next page load
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetching, hasMore]
  );

  let content;

  if (isFetching)
    content = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (isError) {
    content = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          // mt: 10,
          // alignItems: "center",
          // height: "100vh",
          // height: "calc(100vh - 200px)",
        }}
      >
        <Typography variant="subtitle1" component="p">
          {error?.data?.message}
        </Typography>
        {/* Error loading properties */}
      </Box>
    );
  }

  // // Filter properties based on the selected filter status
  // const filteredProperties = data?.entities.filter((property) => {
  //   if (filterStatus === "published") return property.isPublished;
  //   if (filterStatus === "unpublished") return !property.isPublished;
  //   return true; // 'all' case
  // });

  // Handle filter change
  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setPage(1); // Reset page to 1 when filter changes
  };

  if (isSuccess) {
    const propertyData = properties.length
      ? properties.map((property, index) => (
          <MyProperty
            key={property.id}
            property={property}
            lastPropertyRef={
              index === properties.length - 1 ? lastPropertyRef : null
            }
          />
        ))
      : null;
    content = (
      <Container maxWidth="xl" sx={{ marginBottom: 5 }}>
        <Grid2 container spacing={3}>
          {propertyData}
        </Grid2>
        {/* Optionally show a loading spinner when fetching next page */}
        {isFetching && properties.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="xl" sx={{ marginTop: 5, marginBottom: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          // py={2}
          sx={{
            flexDirection: {
              xs: "column", // Column layout on extra-small (xs) screens
              sm: "row", // Row layout on small (sm) screens and above
            },
            gap: 2, // Adds spacing between items in column layout
            px: { md: 3 },
          }}
        >
          {/* Left Side: Title */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              textAlign: { xs: "center", sm: "left" }, // Center title on small screens
            }}
          >
            My Properties
          </Typography>

          {/* Right Side: Filter Buttons */}
          <ButtonGroup
            variant="outlined"
            color="inherit"
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-end" }, // Center buttons on small screens
              flexWrap: "wrap", // Allow buttons to wrap if space is limited
            }}
          >
            <Button
              onClick={() => handleFilterChange("all")}
              // variant={filterStatus === "all" ? "contained" : "outlined"}
              sx={{
                backgroundColor: filterStatus === "all" ? "#e0e0e0" : undefined, // Add border only for active button
              }}
            >
              All Properties
            </Button>
            <Button
              onClick={() => handleFilterChange("published")}
              // variant={
              //   filterStatus === "published" ? "contained" : "outlined"
              // }
              sx={{
                backgroundColor:
                  filterStatus === "published" ? "#e0e0e0" : undefined, // Add border only for active button
              }}
            >
              Published
            </Button>
            <Button
              onClick={() => handleFilterChange("unpublished")}
              // variant={
              //   filterStatus === "unpublished" ? "contained" : "outlined"
              // }
              sx={{
                backgroundColor:
                  filterStatus === "unpublished" ? "#e0e0e0" : undefined, // Add border only for active button
              }}
            >
              Unpublished
            </Button>
          </ButtonGroup>
        </Box>
      </Container>
      <Box sx={{ px: { md: 3 } }}>{content}</Box>

      <Snackbar
        open={snackbar.open}
        // autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          // variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
export default MyPropertiesList;
